import {
  OrderLogTableEntry,
  OrderStatusLog,
  StatusLogRefundDetail,
  EventType,
  ActorType,
  RefundType
} from '../../type'
import {
  formatMoney,
  sanitizeNumber
} from 'packages/@slerp/designer-rework/shop/components/Cart/utils'
import moment from 'moment'

const REFUND_PREFIX: Record<RefundType, string> = {
  [RefundType.FULL]: 'Fully Refunded',
  [RefundType.PARTIAL]: 'Partially Refunded'
}

const generateRefundDescription = (
  refund?: StatusLogRefundDetail | null
): string | null => {
  if (!refund) {
    return null
  }

  const prefix = REFUND_PREFIX[refund.type]

  if (!prefix) {
    return null
  }

  const amount =
    refund.amount != null
      ? ` (${formatMoney(sanitizeNumber(refund.amount))})`
      : ''
  const reason = refund.description ? ` - ${refund.description}` : ''

  return `${prefix}${amount}${reason}`
}

export const composeStatusLog = (
  statusLog: OrderStatusLog[] | undefined | null
): OrderLogTableEntry[] => {
  if (!statusLog) {
    return []
  }

  return statusLog
    .map((log) => {
      const event = getEventBase(log, statusLog)

      if (!event) {
        return null
      }

      return {
        id: log.id,
        date: moment(log.insertedAt).format('DD MMM YYYY'),
        time: moment(log.insertedAt).format('HH:mm'),
        responsible: log.refund?.performedBy || getResponsible(log),
        url: log.url || null,
        ...event
      }
    })
    .filter((log): log is OrderLogTableEntry => log !== null)
}

const getEventBase = (
  log: OrderStatusLog,
  logs: OrderStatusLog[]
): { event: string; description: string | null } | null => {
  switch (log.eventType) {
    case EventType.ORDER_CREATED:
      return {
        event: 'Order Placed',
        description: null
      }
    case EventType.ORDER_ACCEPTED:
      return {
        event: 'Order Accepted',
        description: generateAcceptedDescription(log, logs)
      }
    case EventType.ORDER_COMPLETE:
      return {
        event: 'Order Fulfilled',
        description: null
      }
    case EventType.ORDER_REJECTED:
      return {
        event: 'Order Rejected',
        description: null
      }
    case EventType.ORDER_REFUNDED:
      return {
        event: 'Order Refunded',
        description: generateRefundDescription(log.refund)
      }
    case EventType.CUSTOMER_RECEIPT_EMAIL_SENT:
      return {
        event: 'Confirmation Email sent to Customer',
        description: 'View email here'
      }
    case EventType.STATUS_UPDATE_EMAIL_SENT:
      return {
        event: 'Status Update Email sent to Customer',
        description: 'View email here'
      }
    default:
      return null
  }
}

const getResponsible = (log: OrderStatusLog) => {
  if (log.thirdPartyActorSource) {
    return log.thirdPartyActorSource
  }

  switch (log.actorType) {
    case ActorType.CUSTOMER:
      return 'Customer'
    case ActorType.PARTNER:
      return 'Partner'
    case ActorType.SYSTEM:
      return 'System'
    case ActorType.DISPATCH:
      return 'Ops'
    case ActorType.SLERP:
      return 'Slerp'
  }
}

const formatDuration = (minutes: number): string => {
  const duration = moment.duration(minutes, 'minutes')
  const days = Math.floor(duration.asDays())
  const hours = duration.hours()
  const mins = duration.minutes()

  const parts: string[] = []

  if (days > 0) {
    parts.push(`${days} ${days === 1 ? 'day' : 'days'}`)
  }
  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`)
  }
  if (mins > 0) {
    parts.push(`${mins} ${mins === 1 ? 'minute' : 'minutes'}`)
  }

  if (parts.length === 0) {
    return 'less than a minute'
  }

  if (parts.length === 1) {
    return parts[0]
  }

  if (parts.length === 2) {
    return `${parts[0]} and ${parts[1]}`
  }

  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}

const generateAcceptedDescription = (
  log: OrderStatusLog,
  logs: OrderStatusLog[]
) => {
  const orderCreatedLog = logs.find(
    (log) => log.eventType === EventType.ORDER_CREATED
  )

  if (!orderCreatedLog) {
    return null
  }

  const deltaOrderCreated = moment(log.insertedAt).diff(
    moment(orderCreatedLog.insertedAt),
    'minutes'
  )

  if (deltaOrderCreated < 1) {
    return 'Accepted Immediately'
  }

  const formattedDuration = formatDuration(deltaOrderCreated)
  return `Accepted after ${formattedDuration}`
}
